import { useUser } from "contexts/UserContext";
import { useAuthModals } from "contexts/auth-modals-context";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useCallback, useEffect, useMemo, useState } from "react";

import Loading from "components/Loading";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import { AmplitudePageName } from "utils/amplitude";
import getClasses from "utils/getClasses";
import getFeatureFlagsByOrchidPro from "utils/getFeatureFlagsByOrchidPro";
import getOrchidProDisplayName from "utils/getOrchidProDisplayName";
import { getBusyTimes, getGeneralAvailabilities } from "../orchid-pro/Utils";
import getAppointmentTypes from "services/scheduler/querySessionTypesByProId";
import styles from "./AppointmentBook.module.scss";
import AppointmentTypesSelect, {
  APPOINTMENT_TYPES_SELECT_EMPTY_OPTION,
  AppointmentTypesSelectOption,
} from "./appointment-types-select/AppointmentTypesSelect";
import PatientBookCalendar from "./patient-book-calendar/PatientBookCalendar";
import RequestDifferentTime from "./request-different-time/RequestDifferentTime";

type AppointmentBookProps = {
  appointmentType?: SessionType;
  orchidPro: OrchidPro;
  showThreeRows?: boolean;
  isClientPortal?: boolean;
  show?: boolean;
  onHide?: () => void;
  setSelectedTimeslot?: (arg) => void;
  amplitudePageName?: AmplitudePageName;
  amplitudeComponentName?: string;
};

const AppointmentBook = ({
  orchidPro,
  show,
  onHide,
  setSelectedTimeslot,
  appointmentType,
  showThreeRows,
  isClientPortal,
  amplitudePageName,
  amplitudeComponentName,
}: AppointmentBookProps) => {
  const { user } = useUser();
  const { isPhonePortrait } = usePhonePortraitContext();

  const { openAuthModals } = useAuthModals();
  const [showTimeslotModal, setShowTimeslotModal] = useState(false);
  const [showRequestDifferentTimeModal, setShowRequestDifferentTimeModal] =
    useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const [schedule, setSchedule] = useState<Schedule>([]);
  const [blockedIntervals, setBlockedIntervals] = useState<BlockedInterval[]>(
    [],
  );
  const [appointmentTypes, setAppointmentTypes] = useState<SessionType[]>([]);
  const [selectedType, setSelectedType] =
    useState<AppointmentTypesSelectOption>(
      APPOINTMENT_TYPES_SELECT_EMPTY_OPTION,
    );

  useEffect(() => {
    const abortController = new AbortController();
    async function initData(signal: AbortController["signal"]) {
      setBusy(true);

      const schedule = await getGeneralAvailabilities(orchidPro.cognito_sub, {
        signal,
      });
      setSchedule(schedule);

      const appointmentTypesAll = await getAppointmentTypes(
        orchidPro.cognito_sub,
        { signal },
      );
      const busyTimes = await getBusyTimes(orchidPro.cognito_sub, {
        signal,
      });
      setBlockedIntervals(busyTimes);

      const appointmentTypes = appointmentTypesAll.filter(
        (t) => !t.invite_only,
      );

      setAppointmentTypes(appointmentTypes);

      const freeSessions = appointmentTypes.filter((t) => t.price === 0);
      const initiallySelectedAppointmentType = !orchidPro.can_accept_payment
        ? {}
        : getFeatureFlagsByOrchidPro(orchidPro).dontPreselectAppointmentType
        ? APPOINTMENT_TYPES_SELECT_EMPTY_OPTION
        : freeSessions.find((t) => t.label.toLowerCase().includes("intro")) ||
          freeSessions[0] ||
          appointmentTypes[0];

      setSelectedType(
        appointmentType ? appointmentType : initiallySelectedAppointmentType,
      );
    }
    initData(abortController.signal)
      .catch((error: any) => setError(error.toString()))
      .finally(() => setBusy(false));
    return () => abortController.abort();
  }, [appointmentType, orchidPro]);

  const openTimeslotModal = useCallback(() => {
    if (!user?.signup_form_finished) {
      openAuthModals();
      return;
    }

    setShowTimeslotModal(true);
  }, [openAuthModals, user?.signup_form_finished]);

  const openRequestDifferentTimeModal = useCallback(() => {
    if (!user?.signup_form_finished) {
      openAuthModals();
      return;
    }

    setShowRequestDifferentTimeModal(true);
  }, [openAuthModals, user?.signup_form_finished]);

  const shouldUseModalLayout = useMemo(
    () => showTimeslotModal || show,
    [showTimeslotModal, show],
  );

  if (busy) {
    return <Loading />;
  }

  if (shouldUseModalLayout) {
    return (
      <ModalLayout
        className={getClasses(
          isPhonePortrait && styles["appointment-book-modal--phone"],
        )}
        headerClass={
          !isPhonePortrait
            ? styles["appointment-book-modal__header"]
            : styles["appointment-book-modal__header--phone"]
        }
        bodyClass={
          !isPhonePortrait
            ? styles["appointment-book-modal__body"]
            : styles["appointment-book-modal__body--phone"]
        }
        size="lg"
        centered
        show={true}
        onHide={() => (onHide ? onHide() : setShowTimeslotModal(false))}
        Header={`${getOrchidProDisplayName(orchidPro)}'s availability`}
      >
        {/* Timeslot Modal */}
        <PatientBookCalendar
          orchidPro={orchidPro}
          schedule={schedule}
          appointmentType={selectedType}
          busyTimes={blockedIntervals}
          isInModal={true}
          amplitudePageName={amplitudePageName}
          appointmentTypes={appointmentTypes}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
        />
      </ModalLayout>
    );
  }

  return (
    <>
      {error && <span>{error}</span>}
      <AppointmentTypesSelect
        appointmentTypes={appointmentTypes}
        orchidPro={orchidPro}
        selectedType={selectedType}
        amplitudeComponentName={amplitudeComponentName}
        amplitudePageName={amplitudePageName}
        setSelectedType={setSelectedType}
      />
      {/* Timeslot Card */}
      <PatientBookCalendar
        showThreeRows={showThreeRows}
        isClientPortal={isClientPortal}
        orchidPro={orchidPro}
        schedule={schedule}
        appointmentType={selectedType}
        busyTimes={blockedIntervals}
        openTimeslotModal={openTimeslotModal}
        openRequestDifferentTimeModal={openRequestDifferentTimeModal}
        setSelectedTimeslot={setSelectedTimeslot}
        amplitudePageName={amplitudePageName}
        appointmentTypes={appointmentTypes}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />

      <RequestDifferentTime
        show={showRequestDifferentTimeModal}
        onHide={() => setShowRequestDifferentTimeModal(false)}
        orchidPro={orchidPro}
        amplitudePageName={amplitudePageName}
      />
    </>
  );
};

export default AppointmentBook;
